import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Splash from '@/pages/Splash';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import CompleteProfile from '@/pages/CompleteProfile';
import RiderDashboard from '@/pages/RiderDashboard';
import DriverDashboard from '@/pages/DriverDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import RidesAvailable from '@/pages/RidesAvailable';
import DriverAnalytics from '@/pages/DriverAnalytics';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import DeleteAccount from '@/pages/DeleteAccount';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import '@/App.css';

function ProtectedRoute({ children, role, allowIncomplete = false }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-neon-cyan text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Redirect to complete profile if profile is incomplete (for social login users)
  if (!allowIncomplete && user.profile_complete === false) {
    return <Navigate to="/complete-profile" replace />;
  }
  
  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  
  // Determine where to redirect logged-in users
  const getRedirectPath = () => {
    if (!user) return '/auth';
    if (user.profile_complete === false) return '/complete-profile';
    return `/${user.role}`;
  };

  // Splash screen disabled for web - kept for future mobile app
  // const hasSeenSplash = sessionStorage.getItem('vgoride_splash_seen');
  
  return (
    <Routes>
      {/* Splash route kept for future mobile app use */}
      <Route path="/splash" element={<Splash />} />
      <Route 
        path="/" 
        element={user ? <Navigate to={getRedirectPath()} replace /> : <Landing />} 
      />
      <Route path="/auth" element={user ? <Navigate to={getRedirectPath()} replace /> : <Auth />} />
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute allowIncomplete={true}>
            <CompleteProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rider"
        element={
          <ProtectedRoute role="rider">
            <RiderDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver"
        element={
          <ProtectedRoute role="driver">
            <DriverDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute role="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rides-available"
        element={
          <ProtectedRoute>
            <RidesAvailable />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/analytics"
        element={
          <ProtectedRoute role="driver">
            <DriverAnalytics />
          </ProtectedRoute>
        }
      />
      {/* Public pages - no auth required */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;